/**
 * Hook for polling transaction status after 3DS redirect.
 * Automatically stops on terminal statuses (Captured/Failed) or timeout.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useGetTransactionStatus } from '../services';
import { POLLING_INTERVAL_MS, POLLING_TIMEOUT_MS, TERMINAL_STATUSES } from '../constants';
import { TransactionStatus } from '../types';

interface UseTransactionPollingOptions {
  transactionGuid: string | undefined;
  enabled?: boolean;
}

interface UseTransactionPollingResult {
  status: TransactionStatus | null;
  isPolling: boolean;
  isTimedOut: boolean;
  error: Error | null;
  retry: () => void;
}

export const useTransactionPolling = ({
  transactionGuid,
  enabled = true,
}: UseTransactionPollingOptions): UseTransactionPollingResult => {
  const [isPolling, setIsPolling] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [lastStatus, setLastStatus] = useState<TransactionStatus | null>(null);
  const isTerminal = lastStatus ? (TERMINAL_STATUSES as readonly string[]).includes(lastStatus) : false;
  const shouldPoll = !!transactionGuid && enabled && !isTimedOut && !isTerminal;

  const { data, error, refetch } = useGetTransactionStatus(transactionGuid, {
    enabled: shouldPoll,
    refetchInterval: shouldPoll ? POLLING_INTERVAL_MS : false,
  });

  const status = data?.status ?? lastStatus;

  // Track the latest status so we can stop polling on terminal statuses
  useEffect(() => {
    if (data?.status) {
      setLastStatus(data.status);
    }
  }, [data?.status]);

  // Start/stop polling state
  useEffect(() => {
    if (shouldPoll && !isTerminal) {
      setIsPolling(true);
    } else {
      setIsPolling(false);
    }
  }, [shouldPoll, isTerminal]);

  // Timeout handler
  useEffect(() => {
    if (shouldPoll && !isTerminal) {
      timeoutRef.current = setTimeout(() => {
        setIsTimedOut(true);
        setIsPolling(false);
      }, POLLING_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [shouldPoll, isTerminal]);

  const retry = useCallback(() => {
    setIsTimedOut(false);
    refetch();
  }, [refetch]);

  return {
    status,
    isPolling: isPolling && !isTerminal,
    isTimedOut,
    error: error as Error | null,
    retry,
  };
};
